import { openDB } from "./backend/_db";

const LEGACY_TOY_COLLECTION_STORAGE_KEY = "studentToyCollection:v1";
const STUDENT_LEARNING_STORE_NAME = "student_learning";
export const TOY_PROGRESS_UPDATED_EVENT = "toy-progress-updated";

export const TOY_SLOT_COUNT = 10;
export const TOY_TARGET_PER_SLOT = 10;

export const TOY_SLOT_EMOJIS = [
  "🚗",
  "🪀",
  "🪁",
  "🪄",
  "🧩",
  "🎯",
  "🪅",
  "⚽",
  "🪙",
  "🧸",
];

export const TOY_SLOT_NAMES = [
  "Race Car",
  "Yo-Yo",
  "Kite",
  "Magic Wand",
  "Puzzle",
  "Dart Board",
  "Pinata",
  "Football",
  "Coin",
  "Teddy Bear",
];

type ToyRarity = "Common" | "Rare" | "Epic";

export const TOY_SLOT_RARITIES: ToyRarity[] = [
  "Common",
  "Common",
  "Rare",
  "Rare",
  "Common",
  "Rare",
  "Epic",
  "Common",
  "Epic",
  "Common",
];

const TOY_RARITY_WEIGHTS: Record<ToyRarity, number> = {
  Common: 70,
  Rare: 23,
  Epic: 7,
};

export type ToyShelfProgress = {
  slots: number[];
  lastCollectedSlot: number | null;
};

type Collectible = {
  [key: string]: number;
};

type StudentLearningRecord = {
  studentId: number;
  words?: unknown[];
  toyProgress?: ToyShelfProgress;
  collectible?: Collectible;
  [key: string]: unknown;
};

type LegacyToyCollectionStore = Record<string, ToyShelfProgress>;

const createEmptyProgress = (): ToyShelfProgress => ({
  slots: Array.from({ length: TOY_SLOT_COUNT }, () => 0),
  lastCollectedSlot: null,
});

const createDefaultCollectible = (): Collectible => ({});

const collectibleKeyFromSlot = (slotIndex: number): string => {
  const toyName = TOY_SLOT_NAMES[slotIndex] ?? `Toy ${slotIndex + 1}`;
  return toyName.trim().toLowerCase();
};

const deriveCollectibleFromProgress = (progress: ToyShelfProgress): Collectible => {
  const collectible: Collectible = {};

  progress.slots.forEach((count, slotIndex) => {
    const normalizedCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    if (normalizedCount <= 0) return;

    collectible[collectibleKeyFromSlot(slotIndex)] = normalizedCount;
  });

  return collectible;
};

const normalizeCollectible = (value: unknown): Collectible => {
  if (!value || typeof value !== "object") return createDefaultCollectible();

  const output: Collectible = {};
  for (const [key, rawCount] of Object.entries(value as Record<string, unknown>)) {
    const count = Number(rawCount);
    if (!Number.isFinite(count) || count < 0) continue;
    output[key] = Math.floor(count);
  }

  return output;
};

const getWeightedRandomAvailableSlot = (slots: number[]): number | null => {
  const slotsByRarity: Record<ToyRarity, number[]> = {
    Common: [],
    Rare: [],
    Epic: [],
  };

  slots.forEach((count, slotIndex) => {
    if (count >= TOY_TARGET_PER_SLOT) return;
    const rarity = TOY_SLOT_RARITIES[slotIndex] ?? "Common";
    slotsByRarity[rarity].push(slotIndex);
  });

  const availableRarities = (Object.keys(slotsByRarity) as ToyRarity[]).filter(
    (rarity) => slotsByRarity[rarity].length > 0
  );

  if (availableRarities.length === 0) return null;

  const totalWeight = availableRarities.reduce(
    (sum, rarity) => sum + TOY_RARITY_WEIGHTS[rarity],
    0
  );

  let roll = Math.random() * totalWeight;
  let selectedRarity: ToyRarity = availableRarities[availableRarities.length - 1] ?? "Common";

  for (const rarity of availableRarities) {
    roll -= TOY_RARITY_WEIGHTS[rarity];
    if (roll <= 0) {
      selectedRarity = rarity;
      break;
    }
  }

  const candidateSlots = slotsByRarity[selectedRarity];
  const randomIndex = Math.floor(Math.random() * candidateSlots.length);
  return candidateSlots[randomIndex] ?? null;
};

const normalizeProgress = (value: unknown): ToyShelfProgress => {
  const fallback = createEmptyProgress();
  if (!value || typeof value !== "object") return fallback;

  const maybeProgress = value as Partial<ToyShelfProgress>;
  const slots = Array.isArray(maybeProgress.slots)
    ? maybeProgress.slots
        .slice(0, TOY_SLOT_COUNT)
        .map((count) =>
          Number.isFinite(count)
            ? Math.max(0, Math.min(TOY_TARGET_PER_SLOT, Number(count)))
            : 0
        )
    : [];

  while (slots.length < TOY_SLOT_COUNT) {
    slots.push(0);
  }

  const lastCollectedSlot =
    typeof maybeProgress.lastCollectedSlot === "number" &&
    maybeProgress.lastCollectedSlot >= 0 &&
    maybeProgress.lastCollectedSlot < TOY_SLOT_COUNT
      ? maybeProgress.lastCollectedSlot
      : null;

  return {
    slots,
    lastCollectedSlot,
  };
};

const toStudentId = (studentId?: number | null): number | null =>
  typeof studentId === "number" && studentId > 0 ? studentId : null;

const readStudentLearningRecord = async (
  studentId: number
): Promise<StudentLearningRecord | null> => {
  const db = await openDB();

  return new Promise((resolve) => {
    const transaction = db.transaction([STUDENT_LEARNING_STORE_NAME], "readonly");
    const store = transaction.objectStore(STUDENT_LEARNING_STORE_NAME);
    const request = store.get(studentId);

    request.onsuccess = () => {
      resolve((request.result as StudentLearningRecord | undefined) ?? null);
    };
    request.onerror = () => {
      resolve(null);
    };
  });
};

const readAllStudentLearningRecords = async (): Promise<StudentLearningRecord[]> => {
  const db = await openDB();

  return new Promise((resolve) => {
    const transaction = db.transaction([STUDENT_LEARNING_STORE_NAME], "readonly");
    const store = transaction.objectStore(STUDENT_LEARNING_STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve((request.result as StudentLearningRecord[]) ?? []);
    };
    request.onerror = () => {
      resolve([]);
    };
  });
};

const writeStudentLearningRecord = async (
  record: StudentLearningRecord
): Promise<void> => {
  const db = await openDB();

  await new Promise<void>((resolve) => {
    const transaction = db.transaction([STUDENT_LEARNING_STORE_NAME], "readwrite");
    const store = transaction.objectStore(STUDENT_LEARNING_STORE_NAME);
    const request = store.put(record);

    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
  });
};

const ensureLearningRecordShape = (
  existing: StudentLearningRecord | null,
  studentId: number
): StudentLearningRecord => {
  const words = Array.isArray(existing?.words) ? existing.words : [];
  const toyProgress = normalizeProgress(existing?.toyProgress);
  const normalizedCollectible = normalizeCollectible(existing?.collectible);
  const collectible =
    Object.keys(normalizedCollectible).length > 0
      ? normalizedCollectible
      : deriveCollectibleFromProgress(toyProgress);

  return {
    ...(existing ?? {}),
    studentId,
    words,
    toyProgress,
    collectible,
  };
};

const parseLegacyStore = (raw: string): LegacyToyCollectionStore => {
  try {
    const parsed = JSON.parse(raw) as LegacyToyCollectionStore;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const migrateLegacyToyStore = async (): Promise<void> => {
  if (typeof window === "undefined") return;

  const raw = localStorage.getItem(LEGACY_TOY_COLLECTION_STORAGE_KEY);
  if (!raw) return;

  const legacyStore = parseLegacyStore(raw);
  const existingRecords = await readAllStudentLearningRecords();
  const existingByStudentId = new Map(
    existingRecords.map((record) => [record.studentId, record])
  );

  for (const [studentKey, value] of Object.entries(legacyStore)) {
    const studentId = Number(studentKey);
    if (!Number.isFinite(studentId) || studentId <= 0) continue;

    const existing = existingByStudentId.get(studentId) ?? null;
    const normalizedRecord = ensureLearningRecordShape(existing, studentId);
    normalizedRecord.toyProgress = normalizeProgress(value);
    normalizedRecord.collectible = deriveCollectibleFromProgress(normalizedRecord.toyProgress);
    await writeStudentLearningRecord(normalizedRecord);
  }

  localStorage.removeItem(LEGACY_TOY_COLLECTION_STORAGE_KEY);
};

let legacyMigrationPromise: Promise<void> | null = null;

const ensureLegacyToyMigration = async (): Promise<void> => {
  if (!legacyMigrationPromise) {
    legacyMigrationPromise = migrateLegacyToyStore();
  }
  await legacyMigrationPromise;
};

export const getToyShelfProgress = async (
  studentId?: number | null
): Promise<ToyShelfProgress> => {
  const resolvedStudentId = toStudentId(studentId);
  if (!resolvedStudentId) return createEmptyProgress();

  await ensureLegacyToyMigration();
  const existing = await readStudentLearningRecord(resolvedStudentId);
  const normalized = ensureLearningRecordShape(existing, resolvedStudentId);

  if (
    !existing ||
    !Array.isArray(existing.words) ||
    !existing.toyProgress ||
    !existing.collectible
  ) {
    await writeStudentLearningRecord(normalized);
  }

  return normalizeProgress(normalized.toyProgress);
};

export const collectToyForWord = async (
  studentId: number | null | undefined,
  _wordCategory: string | null | undefined,
  _wordEnglish: string | null | undefined
): Promise<ToyShelfProgress> => {
  const resolvedStudentId = toStudentId(studentId);
  if (!resolvedStudentId) return createEmptyProgress();

  await ensureLegacyToyMigration();
  const existing = await readStudentLearningRecord(resolvedStudentId);
  const normalizedRecord = ensureLearningRecordShape(existing, resolvedStudentId);
  const current = normalizeProgress(normalizedRecord.toyProgress);
  const nextSlots = [...current.slots];
  const slot = getWeightedRandomAvailableSlot(nextSlots);

  if (slot === null) {
    const nextProgress: ToyShelfProgress = {
      slots: nextSlots,
      lastCollectedSlot: null,
    };

    normalizedRecord.toyProgress = nextProgress;
    normalizedRecord.collectible = deriveCollectibleFromProgress(nextProgress);
    await writeStudentLearningRecord(normalizedRecord);

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TOY_PROGRESS_UPDATED_EVENT));
    }
    return nextProgress;
  }

  nextSlots[slot] += 1;

  const nextProgress: ToyShelfProgress = {
    slots: nextSlots,
    lastCollectedSlot: slot,
  };

  normalizedRecord.toyProgress = nextProgress;
  normalizedRecord.collectible = deriveCollectibleFromProgress(nextProgress);
  await writeStudentLearningRecord(normalizedRecord);

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TOY_PROGRESS_UPDATED_EVENT));
  }

  return nextProgress;
};
